import { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Plus, X, Pencil } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { logger } from '../utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'reception',
    client_id: ''
  });

  useEffect(() => {
    fetchUsers();
    fetchClients();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      logger.error('Error fetching users:', error);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/clients`);
      setClients(response.data);
    } catch (error) {
      logger.error('Error fetching clients:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validar que si es cliente, debe tener client_id
    if (formData.role === 'client' && !formData.client_id) {
      toast.error('Debes seleccionar un cliente para asociar');
      return;
    }

    try {
      if (editingUser) {
        // Update existing user
        const updateData = {
          name: formData.name,
          email: formData.email,
          role: formData.role,
          client_id: formData.role === 'client' ? formData.client_id : null
        };
        
        // Only include password if it's not empty
        if (formData.password && formData.password.trim() !== '') {
          updateData.password = formData.password;
        }
        
        await axios.put(`${API}/users/${editingUser.id}`, updateData);
        toast.success('Usuario actualizado exitosamente');
      } else {
        // Create new user
        const createData = {
          ...formData,
          client_id: formData.role === 'client' ? formData.client_id : null
        };
        await axios.post(`${API}/auth/register`, createData);
        toast.success('Usuario creado exitosamente');
      }
      
      setShowForm(false);
      setEditingUser(null);
      setFormData({ email: '', password: '', name: '', role: 'reception', client_id: '' });
      fetchUsers();
    } catch (error) {
      logger.error('Error saving user:', error);
      const errorMessage = error.response?.data?.detail || (editingUser ? 'Error al actualizar usuario' : 'Error al crear usuario');
      toast.error(errorMessage);
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      password: '',
      role: user.role,
      client_id: user.client_id || ''
    });
    setShowForm(true);
  };

  const handleCancelEdit = () => {
    setShowForm(false);
    setEditingUser(null);
    setFormData({ email: '', password: '', name: '', role: 'reception', client_id: '' });
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client ? client.name : '-';
  };

  return (
    <Layout>
      <div className="space-y-8">
        <div className="flex items-center justify-between">
          <h1 className="text-4xl sm:text-5xl font-black tracking-tighter">Usuarios</h1>
          {!showForm && (
            <Button
              onClick={() => setShowForm(true)}
              data-testid="toggle-form-button"
              className="bg-primary hover:bg-primary/90"
            >
              <Plus size={20} className="mr-2" weight="bold" />
              Nuevo Usuario
            </Button>
          )}
        </div>

        {showForm && (
          <div className="p-8 border border-border bg-background">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl sm:text-2xl font-semibold tracking-tight">
                {editingUser ? 'Editar Usuario' : 'Nuevo Usuario'}
              </h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCancelEdit}
                data-testid="close-form-button"
              >
                <X size={20} />
              </Button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="user-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-xs font-bold uppercase tracking-[0.2em]">Nombre *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    data-testid="name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-xs font-bold uppercase tracking-[0.2em]">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    data-testid="email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-xs font-bold uppercase tracking-[0.2em]">
                    Contraseña {editingUser ? '(dejar vacío para mantener la actual)' : '*'}
                  </Label>
                  <Input
                    id="password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required={!editingUser}
                    placeholder={editingUser ? 'Dejar vacío para no cambiar' : ''}
                    data-testid="password-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="role" className="text-xs font-bold uppercase tracking-[0.2em]">Rol *</Label>
                  <select
                    id="role"
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value, client_id: '' })}
                    data-testid="role-select"
                    className="w-full px-4 py-2 border border-border bg-background text-foreground"
                    required
                  >
                    <option value="reception">Recepción</option>
                    <option value="technician">Técnico</option>
                    <option value="admin">Administrador</option>
                    <option value="client">Cliente (Solo Lectura)</option>
                  </select>
                </div>
                
                {/* Campo para asociar cliente cuando el rol es "client" */}
                {formData.role === 'client' && (
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="client_id" className="text-xs font-bold uppercase tracking-[0.2em]">
                      Cliente Asociado *
                    </Label>
                    <select
                      id="client_id"
                      value={formData.client_id}
                      onChange={(e) => setFormData({ ...formData, client_id: e.target.value })}
                      data-testid="client-select"
                      className="w-full px-4 py-2 border border-border bg-background text-foreground"
                      required
                    >
                      <option value="">-- Selecciona un cliente --</option>
                      {clients.map((client) => (
                        <option key={client.id} value={client.id}>
                          {client.name} - {client.phone}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-muted-foreground">
                      El usuario solo podrá ver las órdenes de este cliente
                    </p>
                  </div>
                )}
              </div>
              <Button type="submit" data-testid="submit-user-button" className="bg-primary hover:bg-primary/90">
                {editingUser ? 'Actualizar Usuario' : 'Crear Usuario'}
              </Button>
            </form>
          </div>
        )}

        <div className="border border-border bg-background">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-border">
                <tr className="bg-secondary">
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-[0.2em]">Nombre</th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-[0.2em]">Email</th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-[0.2em]">Rol</th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-[0.2em]">Cliente Asociado</th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-[0.2em]">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {users.length > 0 ? (
                  users.map((user) => (
                    <tr key={user.id} className="hover:bg-secondary/50" data-testid={`user-row-${user.id}`}>
                      <td className="px-6 py-4 font-semibold">{user.name}</td>
                      <td className="px-6 py-4 font-mono text-sm">{user.email}</td>
                      <td className="px-6 py-4">
                        <span className={`inline-block px-3 py-1 text-xs font-bold uppercase tracking-wider border ${
                          user.role === 'client' 
                            ? 'border-blue-300 bg-blue-50 text-blue-700' 
                            : 'border-border bg-secondary'
                        }`}>
                          {user.role === 'admin' && 'Administrador'}
                          {user.role === 'reception' && 'Recepción'}
                          {user.role === 'technician' && 'Técnico'}
                          {user.role === 'client' && 'Cliente'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm">
                        {user.client_id ? getClientName(user.client_id) : '-'}
                      </td>
                      <td className="px-6 py-4">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditUser(user)}
                          data-testid={`edit-user-${user.id}`}
                          className="hover:bg-primary hover:text-primary-foreground"
                        >
                          <Pencil size={16} className="mr-2" />
                          Editar
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center text-muted-foreground">
                      No hay usuarios
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
};
